<template>
  <header :class="[$style['header'], $style[`header--${state}`], solid && $style['header--solid'], $slots.default && $style['header--extra']]">
    <NuxtLink to="/" :class="$style['header__logo']" aria-label="Hengelose Revue, naar boven">
      <Logo />
    </NuxtLink>
    <nav :class="$style['header__nav']" aria-label="Snelmenu">
      <NuxtLink v-for="item in inlineItems" :key="item.to" :to="item.to" :aria-current="current(item.to)">{{ item.name }}</NuxtLink>
    </nav>
    <div :class="$style['header__actions']">
      <slot />
      <Button v-if="tickets" variant="primary" :to="ticketsTo" :class="$style['header__tickets']">Kaarten</Button>
      <!-- The icon is drawn here rather than by Button, so the padding stays even when the label is hidden -->
      <Button
        variant="outline"
        :class="$style['header__menu']"
        aria-controls="home-menu"
        :aria-expanded="menuOpen"
        @click="menuOpen = true">
        <Icon name="Menu" :class="$style['header__menu-icon']" />
        <span :class="$style['header__menu-label']">Menu</span>
      </Button>
    </div>
    <SiteMenu v-model:open="menuOpen" :items="items" :tickets-to="ticketsTo" />
  </header>
</template>
<script setup lang="ts">
// On the homepage: 'css' until the page has loaded (appears by itself after the curtain), 'hidden' while the curtain is closed.
// Elsewhere the bar turns black once you scroll, so it stays readable over the text.
// The rental pages leave the Kaarten button out of the bar (it is still in the menu).
const props = withDefaults(defineProps<{
  state?: 'css' | 'hidden' | 'visible'
  tickets?: boolean
  ticketsTo?: string
  solidOnScroll?: boolean
}>(), { state: 'visible', tickets: true, ticketsTo: '/kaartverkoop', solidOnScroll: true })

const route = useRoute()
const { navigation } = useNavigation()
const menuOpen = ref(false)
const solid = ref(false)

const onScroll = () => { solid.value = window.scrollY > 16 }

// 'page' on the page itself, 'true' anywhere in its section
const current = (to: string) => {
  if (route.path === to) return 'page'
  return to !== '/' && route.path.startsWith(`${to}/`) ? 'true' : undefined
}

onMounted(() => {
  if (!props.solidOnScroll) return
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
})

onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))

const items = computed(() => ((navigation.value as any)?.items ?? [])
  .filter((item: any) => item.parent?.full_slug)
  .map((item: any) => ({
    name: item.parent.name,
    to: `/${item.parent.full_slug}`.replace(/\/$/, '') || '/',
    children: (item.items ?? [])
      .filter((child: any) => child?.full_slug)
      .map((child: any) => ({ name: child.name, to: `/${child.full_slug}`.replace(/\/$/, '') }))
  })))

// Tickets have their own button
const inlineItems = computed(() => items.value.filter((item: { to: string }) => item.to !== '/kaartverkoop'))
</script>
<style module>
.header {
  position: fixed;
  top: env(safe-area-inset-top, 0);
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
  padding: var(--spacing-xl) var(--stage-gutter);
  color: var(--stage-white);
  pointer-events: none;
}

.header > * {
  pointer-events: auto;
}

/* Keeps the links readable over the trailer */
.header::before {
  content: "";
  position: absolute;
  inset: 0 0 -2.5rem;
  z-index: -1;
  pointer-events: none;
  background: linear-gradient(oklch(0% 0 0deg / 60%), oklch(0% 0 0deg / 0%));
  transition: opacity 300ms;
}

.header::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: var(--stage-black);
  border-bottom: var(--stage-hairline) solid var(--stage-line);
  opacity: 0;
  transition: opacity 300ms;
}

.header--solid::before {
  opacity: 0;
}

.header--solid::after {
  opacity: 1;
}

.header--css {
  animation: header-in 0.6s ease 2.4s both;
}

.header--hidden {
  opacity: 0;
  visibility: hidden;
}

.header--visible {
  opacity: 1;
  visibility: visible;
  transition: opacity 600ms, visibility 600ms;
}

@keyframes header-in {
  from {
    opacity: 0;
    visibility: hidden;
  }
}

.header__logo {
  display: block;
  flex: none;
  width: 12rem;
}

.header__nav {
  display: flex;
  gap: var(--spacing-xl);
  margin-left: auto;
  margin-right: var(--spacing-l);
}

.header__nav a {
  padding-block: var(--spacing);
  white-space: nowrap;
  color: var(--stage-white);
  font-size: var(--font-size-base);
  text-decoration: none;
  text-shadow: 0 0.0625rem 0.75rem oklch(0% 0 0deg / 60%);
}

.header__nav a:hover,
.header__nav a[aria-current] {
  color: var(--stage-white);
  text-decoration: underline;
  text-decoration-color: var(--primary-color);
  text-decoration-thickness: 0.125rem;
  text-underline-offset: 0.4em;
}

.header__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-m);
}

.header__tickets,
.header__menu {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.header__menu {
  box-shadow: inset 0 0 0 0.125rem var(--stage-white);
}

/* With an extra button (the rental pages) the links make way sooner */
@media (width <= 78rem) {
  .header--extra .header__nav {
    display: none;
  }

  .header--extra .header__actions {
    margin-left: auto;
  }
}

.header__menu-icon {
  flex: none;
  width: 1.25rem;
  height: 1.25rem;
}

.header__menu-label {
  margin-left: var(--spacing);
}

@media (width <= 68rem) {
  .header__nav {
    display: none;
  }

  .header__actions {
    margin-left: auto;
  }
}

/* On phones Kaarten lives in the menu, so the bar keeps only the logo and the menu */
@media (width <= 48rem) {
  .header .header__tickets {
    display: none;
  }
}

@media (width <= 30rem) {
  .header {
    gap: var(--spacing-m);
    padding-inline: var(--spacing-l);
  }

  .header__logo {
    width: 9.5rem;
  }

  .header__actions {
    gap: var(--spacing);
  }

  /* Compacter buttons, so the logo keeps its size */
  .header .header__menu {
    --padding-x: var(--spacing-l);
  }
}

@media (width <= 23rem) {
  .header__logo {
    width: 8.5rem;
  }
}

/* The smallest phones with an extra button (the rental pages): the menu button shows only its icon */
@media (width <= 21rem) {
  .header--extra .header__menu-label {
    position: absolute;
    width: var(--stage-hairline);
    height: var(--stage-hairline);
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}

/* While the links are in the bar, the bar is the menu: the menu button only appears once they make way */
@media (width > 78rem) {
  .header .header__menu {
    display: none;
  }
}

@media (width > 68rem) {
  .header:not(.header--extra) .header__menu {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .header--css {
    animation: none;
  }

  .header::before,
  .header::after {
    transition: none;
  }
}
</style>
